import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { getOfferById } from '../services/offerService';
import NavBar from '../components/NavBar';
import { ArrowLeft, Clock, Tag, MapPin, DollarSign, Edit, MoreVertical } from 'lucide-react';
import MediaDisplay from '../components/MediaDisplay';
import MediaViewer from '../components/MediaViewer';
import DownloadableFileAttachment from '../components/DownloadableFileAttachment';
import '../styles/media.css';
import { useAuth } from '../contexts/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from 'sonner';
import OfferForm from '../components/OfferForm';

const OfferView = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showMediaViewer, setShowMediaViewer] = useState(false);
  const [mediaViewerIndex, setMediaViewerIndex] = useState(0);
  const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8080';
  const { user } = useAuth();
  const [showEditForm, setShowEditForm] = useState(false);

  useEffect(() => {
    const fetchOffer = async () => {
      try {
        setLoading(true);
        const data = await getOfferById(id);
        setOffer(data);
      } catch (err) {
        setError('Failed to load offer details');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    // If we have offer data in location state, use it
    if (location.state?.offer) {
      setOffer(location.state.offer);
      setLoading(false);
    } else if (id) {
      fetchOffer();
    }
  }, [id, location.state]);

  useEffect(() => {
    if (offer) {
      console.log("Offer data:", offer);
      console.log("Media in offer:", offer.media);
      console.log("Processed media:", processedMedia);
      console.log("Visual media:", visualMedia);
      console.log("Document media:", documentMedia);
    }
  }, [offer]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <NavBar />
        <main className="container mx-auto px-4 pt-24 pb-12">
          <div className="max-w-2xl mx-auto">
            <p className="text-center">Loading offer details...</p>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white">
        <NavBar />
        <main className="container mx-auto px-4 pt-24 pb-12">
          <div className="max-w-2xl mx-auto">
            <p className="text-center text-red-500">{error}</p>
          </div>
        </main>
      </div>
    );
  }

  if (!offer) {
    return (
      <div className="min-h-screen bg-white">
        <NavBar />
        <main className="container mx-auto px-4 pt-24 pb-12">
          <div className="max-w-2xl mx-auto">
            <p className="text-center">Offer not found</p>
          </div>
        </main>
      </div>
    );
  }

  // Process media to ensure URLs are complete
  const processedMedia = offer.media?.map(item => ({
    ...item,
    url: item.url.startsWith('http') ? item.url : `${baseUrl}${item.url}`,
    id: item.id || `media-${Math.random()}`,
    type: item.type || (item.mimeType?.startsWith('image/') ? 'image' : 
           item.mimeType?.startsWith('video/') ? 'video' : 'document'),
    name: item.name || 'File',
    mimeType: item.mimeType || 'application/octet-stream'
  })) || [];

  // Separate visual media from documents
  const visualMedia = processedMedia.filter(item => 
    item.type === 'image' || 
    item.type === 'video' || 
    item.mimeType?.startsWith('image/') || 
    item.mimeType?.startsWith('video/')
  );

  const documentMedia = processedMedia.filter(item => 
    (item.type !== 'image' && item.type !== 'video') && 
    (!item.mimeType?.startsWith('image/') && !item.mimeType?.startsWith('video/'))
  );

  const handleEdit = () => {
    setShowEditForm(true);
  };

  const isOfferCreator = user?.userId === offer.providerId;

  return (
    <div className="min-h-screen bg-white">
      <NavBar />
      <main className="container mx-auto px-4 pt-24 pb-12">
        <div className="max-w-2xl mx-auto">
          <button 
            onClick={() => navigate(-1)} 
            className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </button>

          <div className="bg-white rounded-lg overflow-hidden">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h1 className="text-2xl font-bold text-gray-900">{offer.title}</h1>
                
                {isOfferCreator && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="p-2 rounded-full hover:bg-gray-100">
                        <MoreVertical className="h-5 w-5 text-gray-500" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={handleEdit}>
                        <Edit className="h-4 w-4 mr-2" />
                        Edit Offer
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
              
              {offer.updatedAt && offer.updatedAt !== offer.createdAt && (
                <div className="text-xs text-gray-500 italic mt-1">
                  Edited: {new Date(offer.updatedAt).toLocaleString()}
                </div>
              )}

              <div className="flex flex-wrap gap-2 mb-6">
                <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">
                  {offer.status}
                </span>
                {offer.price > 0 && (
                  <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                    {offer.currency} {offer.price}
                  </span>
                )}
                {offer.estimatedCompletionDate && (
                  <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                    Due: {new Date(offer.estimatedCompletionDate).toLocaleDateString()}
                  </span>
                )}
              </div>

              <div className="prose max-w-none mb-6">
                <p className="text-gray-700">{offer.description}</p>
              </div>

              {/* Visual Media Section */}
              {visualMedia.length > 0 && (
                <div className="media-carousel-container mb-6">
                  <MediaDisplay 
                    media={visualMedia}
                    onClick={(index) => {
                      setMediaViewerIndex(index);
                      setShowMediaViewer(true);
                    }}
                    className="mb-6"
                  />
                </div>
              )}

              {/* Document Attachments Section */}
              {documentMedia.length > 0 && (
                <div className="document-attachments mb-6">
                  <div className="flex items-center mb-3">
                    <h3 className="text-sm font-medium text-gray-700">Attachments</h3>
                    <span className="ml-2 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                      {documentMedia.length}
                    </span>
                  </div>
                  <div className="space-y-2 bg-gray-50 p-4 rounded-lg border border-gray-100">
                    {documentMedia.map((doc) => (
                      <DownloadableFileAttachment 
                        key={doc.id || `doc-${Math.random()}`} 
                        file={doc} 
                      />
                    ))}
                  </div>
                </div>
              )}

              <div className="border-t border-gray-100 pt-4 mt-6">
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-500">
                    Offered by: {offer.providerName || 'Anonymous'}
                  </div>
                  <div className="text-sm text-gray-500">
                    Created: {new Date(offer.createdAt).toLocaleDateString()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {showMediaViewer && (
        <MediaViewer
          media={visualMedia}
          initialIndex={mediaViewerIndex}
          onClose={() => setShowMediaViewer(false)}
        />
      )}

      {showEditForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <OfferForm
              willId={offer.willId}
              offerId={offer.id}
              isEditMode={true}
              initialData={{
                description: offer.description,
                price: offer.price?.toString() || '',
                currency: offer.currency || 'ILS',
                estimatedCompletionDate: offer.estimatedCompletionDate || '',
                providerContactInfo: offer.providerContactInfo || ''
              }}
              existingMedia={processedMedia}
              onSuccess={(updatedOffer) => {
                setOffer(updatedOffer);
                setShowEditForm(false);
                toast.success('Offer updated successfully');
              }}
              onCancel={() => setShowEditForm(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default OfferView;
